import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API_URL } from '../utils/config';

export default function ForgotPasswordPage() {
  const [step, setStep] = useState(1); // 1=email, 2=otp+newpass
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const sendOtp = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error('Enter your email');
    if (!/@qualtechedge\.(com|in)$/.test(email)) return toast.error('Use your @qualtechedge.com or @qualtechedge.in email');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email });
      toast.success('OTP sent to your email');
      setStep(2);
    } catch(err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP');
    } finally { setLoading(false); }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (!otp.trim()) return toast.error('Enter the OTP');
    if (newPassword.length < 6) return toast.error('Password must be at least 6 characters');
    if (newPassword !== confirmPassword) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/reset-password`, { email, otp, new_password: newPassword });
      toast.success('Password reset successfully! Please login.');
      setTimeout(() => window.location.href = '/login', 1500);
    } catch(err) {
      toast.error(err.response?.data?.message || 'Invalid or expired OTP');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-slate-900 to-teal-950 px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <img src="/logo.png" alt="Qtalk" className="w-16 h-16 object-contain mb-3" style={{filter:'brightness(0) invert(1)'}}/>
          <h1 className="text-2xl font-black text-white tracking-tight">Qtalk</h1>
          <p className="text-sm text-gray-400 mt-1">QualTechEdge Internal Platform</p>
        </div>

        {/* Card */}
        <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="px-8 pt-8 pb-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-teal-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                  <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-black text-white">Reset Password</h2>
                <p className="text-xs text-gray-400">
                  {step === 1 ? 'Enter your email to receive an OTP' : `OTP sent to ${email}`}
                </p>
              </div>
            </div>

            {/* Step indicator */}
            <div className="flex items-center gap-2 mt-4">
              <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold transition-all ${step >= 1 ? 'bg-teal-500 text-white' : 'bg-white/10 text-gray-400'}`}>1</div>
              <div className={`flex-1 h-0.5 rounded transition-all ${step >= 2 ? 'bg-teal-500' : 'bg-white/10'}`}/>
              <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold transition-all ${step >= 2 ? 'bg-teal-500 text-white' : 'bg-white/10 text-gray-400'}`}>2</div>
            </div>
          </div>

          <div className="px-8 pb-8">
            {/* Step 1 — Email */}
            {step === 1 && (
              <form onSubmit={sendOtp} className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-2">Email Address</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="you@qualtechedge.com" autoFocus
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition text-sm"/>
                </div>
                <button type="submit" disabled={loading}
                  className="w-full py-3 rounded-2xl bg-gradient-to-r from-teal-500 to-blue-500 text-white font-black text-sm hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-teal-500/20">
                  {loading ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/> Sending OTP...</>
                  ) : (
                    <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg> Send OTP</>
                  )}
                </button>
              </form>
            )}

            {/* Step 2 — OTP + New Password */}
            {step === 2 && (
              <form onSubmit={resetPassword} className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-2">OTP Code</label>
                  <input type="text" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g,'').slice(0,6))}
                    placeholder="Enter 6-digit OTP" autoFocus maxLength={6}
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition text-sm text-center tracking-[0.5em] font-bold text-lg"/>
                  <p className="text-xs text-gray-500 mt-1.5 text-center">Check your email inbox for the OTP · Valid for 10 minutes</p>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-2">New Password</label>
                  <div className="relative">
                    <input type={showPass ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)}
                      placeholder="Min 6 characters"
                      className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition text-sm pr-12"/>
                    <button type="button" onClick={() => setShowPass(!showPass)}
                      className="absolute right-4 top-3.5 text-gray-400 hover:text-white transition">
                      {showPass
                        ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      }
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-2">Confirm Password</label>
                  <input type={showPass ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password"
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition text-sm"/>
                </div>
                <button type="submit" disabled={loading}
                  className="w-full py-3 rounded-2xl bg-gradient-to-r from-teal-500 to-blue-500 text-white font-black text-sm hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-teal-500/20">
                  {loading ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/> Resetting...</>
                  ) : (
                    <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> Reset Password</>
                  )}
                </button>
                <button type="button" onClick={() => { setStep(1); setOtp(''); }}
                  className="w-full py-2 text-xs text-gray-400 hover:text-teal-400 transition">
                  ← Use a different email
                </button>
              </form>
            )}

            {/* Back to login */}
            <div className="mt-6 text-center">
              <Link to="/login" className="text-xs text-gray-400 hover:text-teal-400 transition flex items-center justify-center gap-1">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                Back to Login
              </Link>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-gray-600 mt-6">© 2026 QualTechEdge · Qtalk v1.0</p>
      </div>
    </div>
  );
}
